import type { AnyFilterItem } from '@gasbi/shared/web/components/Filter';
import dayjs from 'dayjs';

import {
  type WorkflowVersionListItem,
  WorkflowVersionState,
} from '@/features/workflowBuilder';

export const WorkflowsTab = {
  Published: 'Published',
  Drafts: 'Drafts',
} as const;
export type WorkflowsTab = (typeof WorkflowsTab)[keyof typeof WorkflowsTab];

/**
 * The versions each tab lists: «Опубліковано» — what runs now, one row per
 * workflow; «Чернетки» — everything else, one row per version.
 */
export const TAB_STATES: Record<WorkflowsTab, WorkflowVersionState[]> = {
  [WorkflowsTab.Published]: [WorkflowVersionState.Published],
  [WorkflowsTab.Drafts]: [
    WorkflowVersionState.Draft,
    WorkflowVersionState.Retired,
  ],
};

/**
 * Filter ids double as the list item's field names, so the same `filters`
 * array can go to `buildGridifyQuery` unchanged once the backend pages and
 * filters the list itself.
 */
export const WorkflowsFilterKey = {
  Kind: 'kind',
  State: 'state',
  UpdatedAt: 'updatedAt',
  UpdatedBy: 'updatedBy',
} as const;

const matchesTab = (row: WorkflowVersionListItem, tab: WorkflowsTab) =>
  TAB_STATES[tab].includes(row.state);

/** `StatusGroupFilter` emits a primitive for one value, a complex for several. */
const toValues = (item: AnyFilterItem): unknown[] =>
  Array.isArray(item.value) ? item.value : [item.value];

const matchesFilter = (
  row: WorkflowVersionListItem,
  item: AnyFilterItem,
  tab: WorkflowsTab,
) => {
  switch (item.key) {
    case WorkflowsFilterKey.Kind:
      return toValues(item).includes(row.kind);

    // Offered on «Чернетки» only. The filter drawer keeps a value picked there
    // after switching tabs, and on «Опубліковано» it would hide every row.
    case WorkflowsFilterKey.State:
      return tab !== WorkflowsTab.Drafts || toValues(item).includes(row.state);

    case WorkflowsFilterKey.UpdatedBy:
      return !!row.updatedBy && toValues(item).includes(row.updatedBy.id);

    case WorkflowsFilterKey.UpdatedAt: {
      if (item.type !== 'date') return true;

      const { from, to } = item.value ?? {};
      const updatedAt = dayjs(row.updatedAt);

      // Both bounds are whole days, picked in the user's local time.
      if (from && updatedAt.isBefore(dayjs(from).startOf('day'))) return false;
      if (to && updatedAt.isAfter(dayjs(to).endOf('day'))) return false;

      return true;
    }

    default:
      return true;
  }
};

/** An item the filter drawer cleared still sits in the array, value-less. */
const hasValue = (item: AnyFilterItem) =>
  Array.isArray(item.value) ? item.value.length > 0 : item.value != null;

export const filterWorkflows = (
  rows: WorkflowVersionListItem[],
  tab: WorkflowsTab,
  filters: AnyFilterItem[],
  search: string,
): WorkflowVersionListItem[] => {
  const term = search.trim().toLocaleLowerCase();
  const activeFilters = filters.filter(hasValue);

  return rows.filter(
    row =>
      matchesTab(row, tab) &&
      (!term || row.name.toLocaleLowerCase().includes(term)) &&
      activeFilters.every(item => matchesFilter(row, item, tab)),
  );
};
